import { motionOn } from './motion'

/**
 * The four named springs (APP-DESIGN-DIRECTION 3.2), as the same sampled `linear()` points the CSS tokens use, so a WAAPI
 * move and a CSS transition on the same spring land identically. A spring is always used with its own duration.
 *
 *   snappy  k 520, c 34   320 ms   3% overshoot   presses, toggles, menus, the leading edge of a stretch
 *   settle  k 280, c 30   360 ms   none visible   sheets, cards landing, reorders, route slides, the trailing edge
 *   pop     k 620, c 30   380 ms   9% overshoot   reward glyphs only: checks, badges, "New best"
 *   soft    k 170, c 26   560 ms   none           large surfaces: the reward card, the band arc landing
 */
export type SpringName = 'snappy' | 'settle' | 'pop' | 'soft'

const POINTS: Record<SpringName, readonly number[]> = {
  snappy: [0, 0.052, 0.173, 0.324, 0.478, 0.619, 0.738, 0.834, 0.907, 0.958, 0.993, 1.014, 1.025, 1.029, 1.029, 1.026, 1.022, 1.017, 1.013, 1.009, 1],
  settle: [0, 0.03, 0.104, 0.2, 0.304, 0.408, 0.506, 0.594, 0.672, 0.738, 0.794, 0.841, 0.878, 0.908, 0.932, 0.951, 0.965, 0.976, 0.984, 0.99, 1],
  pop: [0, 0.082, 0.267, 0.485, 0.69, 0.857, 0.976, 1.05, 1.085, 1.093, 1.084, 1.066, 1.046, 1.027, 1.012, 1.001, 0.995, 0.992, 0.991, 0.992, 1],
  soft: [0, 0.051, 0.162, 0.293, 0.422, 0.537, 0.636, 0.717, 0.782, 0.834, 0.875, 0.906, 0.93, 0.948, 0.961, 0.972, 0.979, 0.985, 0.989, 0.992, 1],
}

export const SPRING_MS: Record<SpringName, number> = { snappy: 320, settle: 360, pop: 380, soft: 560 }

/** The CSS `linear()` easing string of a spring (for WAAPI `easing`). */
export function springEasing(name: SpringName): string {
  return `linear(${POINTS[name].join(', ')})`
}

/** The spring as a function of progress 0..1 (piecewise linear through the same points), for sampled frames. */
export function springAt(name: SpringName, progress: number): number {
  const points = POINTS[name]
  if (progress <= 0) return 0
  if (progress >= 1) return 1
  const position = progress * (points.length - 1)
  const index = Math.floor(position)
  const fraction = position - index
  return points[index]! + (points[index + 1]! - points[index]!) * fraction
}

/** Cubic-bezier as a function, for the r5 curves (`--r5-e-out` and friends) in sampled frames. */
export function bezier(x1: number, y1: number, x2: number, y2: number) {
  const curve = (a: number, b: number, t: number) => 3 * a * (1 - t) * (1 - t) * t + 3 * b * (1 - t) * t * t + t * t * t
  return (x: number) => {
    if (x <= 0) return 0
    if (x >= 1) return 1
    let lo = 0
    let hi = 1
    let mid = 0.5
    for (let i = 0; i < 22; i += 1) {
      mid = (lo + hi) / 2
      if (curve(x1, x2, mid) < x) lo = mid
      else hi = mid
    }
    return curve(y1, y2, mid)
  }
}
export const easeOut = bezier(0.16, 1, 0.3, 1)
export const easeInOut = bezier(0.65, 0, 0.35, 1)
export const EASE_OUT = 'cubic-bezier(.16,1,.3,1)'
export const EASE_IO = 'cubic-bezier(.65,0,.35,1)'
export const EASE_IN = 'cubic-bezier(.55,0,.75,.2)'

const running = new WeakMap<Element, Animation>()

export interface SpringToOptions {
  delay?: number
  /** Keep the end state applied after the move (default true). */
  fill?: FillMode
}

/**
 * Moves `element` to `keyframes`' last frame on a named spring, retargetably: if a previous `springTo` move is still running
 * on the element, the new move starts from where the element is right now (its computed transform and opacity), so an
 * interrupted move never jumps. Returns the Animation, or null when motion is off (the caller's final styles apply at once).
 */
export function springTo(element: Element, keyframes: Keyframe[], spring: SpringName, options: SpringToOptions = {}): Animation | null {
  if (!motionOn() || typeof (element as HTMLElement).animate !== 'function') return null
  const previous = running.get(element)
  let frames = keyframes
  if (previous && previous.playState === 'running') {
    const style = getComputedStyle(element)
    const from: Keyframe = {}
    if (keyframes.some((frame) => 'transform' in frame)) from.transform = style.transform === 'none' ? 'none' : style.transform
    if (keyframes.some((frame) => 'opacity' in frame)) from.opacity = style.opacity
    previous.cancel()
    frames = [from, keyframes[keyframes.length - 1]!]
  }
  const animation = (element as HTMLElement).animate(frames, {
    duration: SPRING_MS[spring],
    easing: springEasing(spring),
    delay: options.delay ?? 0,
    fill: options.fill ?? 'both',
  })
  running.set(element, animation)
  animation.onfinish = () => { if (running.get(element) === animation) running.delete(element) }
  return animation
}

/** Two edges on two springs, sampled into frames: the leading edge on snappy (260 ms), the trailing edge on settle (380 ms). */
export const STRETCH = { lead: 260, trail: 380, total: 380 } as const

/** Progress of the leading and trailing edge at `ms` into a stretch. */
export function stretchEdges(ms: number): { lead: number; trail: number } {
  return { lead: springAt('snappy', Math.min(1, ms / STRETCH.lead)), trail: springAt('settle', Math.min(1, ms / STRETCH.trail)) }
}
